class SandlotHeader extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-sg-open]');
    this.desktopMenu = this.querySelector('[data-sg-desktop-menu]');
    this.desktopBackdrop = this.querySelector('.sg-desktop-backdrop');
    this.desktopMedia = matchMedia('(min-width: 1100px)');
    this.desktopHover = matchMedia('(hover: hover) and (pointer: fine)');
    this.desktopNavWrap = this.querySelector('.sg-header__nav-wrap');
    this.querySelectorAll('[data-sg-desktop-target]').forEach(trigger => {
      trigger.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && this.desktopHover.matches) this.openDesktop(trigger, true);
      }, options);
    });
    [this.desktopNavWrap, this.desktopMenu].forEach(region => {
      region.addEventListener('pointerenter', () => clearTimeout(this.desktopCloseTimer), options);
      region.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse' || this.desktopInput !== 'pointer') return;
        if ([this.desktopNavWrap, this.desktopMenu].some(area => event.relatedTarget instanceof Node && area.contains(event.relatedTarget))) return;
        clearTimeout(this.desktopCloseTimer);
        this.desktopCloseTimer = setTimeout(() => {
          if (this.desktopInput === 'pointer' && !this.desktopNavWrap.matches(':hover') && !this.desktopMenu.matches(':hover')) this.closeDesktop();
        }, 180);
      }, options);
    });
    this.addEventListener('click', (event) => {
      const target = event.target.closest('button, a');
      if (!target) return;
      if (target.hasAttribute('data-sg-open')) {
        this.closeDesktop();
        this.showPanel('main');
        this.dialog.showModal();
        document.body.setAttribute('data-sg-menu-open', '');
        this.opener.setAttribute('aria-expanded', 'true');
      }
      if (target.hasAttribute('data-sg-close')) this.dialog.close();
      if (target.hasAttribute('data-sg-panel-target')) this.showPanel(target.dataset.sgPanelTarget, true);
      if (target.hasAttribute('data-sg-back')) this.showPanel('main', true);
      if (target.tagName === 'A' && this.dialog.contains(target)) this.dialog.close();
      if (target.hasAttribute('data-sg-desktop-target')) {
        if (this.desktopTrigger === target && event.detail === 0) this.closeDesktop();
        else this.openDesktop(target, event.detail > 0);
      }
      if (target.hasAttribute('data-sg-desktop-dismiss')) this.closeDesktop(true);
      if (target.tagName === 'A') this.closeDesktop();
    }, options);
    this.dialog.addEventListener('close', () => {
      document.body.removeAttribute('data-sg-menu-open');
      this.opener.setAttribute('aria-expanded', 'false');
      this.opener.focus();
    }, options);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.desktopTrigger) {
        event.preventDefault();
        this.closeDesktop(true);
      }
      const trigger = event.target.closest('[data-sg-desktop-target]');
      if (event.key === 'ArrowDown' && trigger && this.contains(trigger)) {
        event.preventDefault();
        this.openDesktop(trigger);
        this.querySelector(`[data-sg-desktop-panel="${trigger.dataset.sgDesktopTarget}"] a`)?.focus();
      }
    }, options);
    this.desktopBackdrop.addEventListener('click', () => this.closeDesktop(true), options);
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeDesktop();
    }, options);
    document.addEventListener('focusin', event => {
      if (!this.contains(event.target)) this.closeDesktop();
    }, options);
    this.desktopMedia.addEventListener('change', () => {
      this.closeDesktop();
      if (this.desktopMedia.matches && this.dialog.open) this.dialog.close();
    }, options);
    window.addEventListener('resize', () => { if (this.desktopTrigger) this.positionDesktop(); }, options);
  }
  positionDesktop() {
    const banner = this.querySelector('.sg-header__nav').getBoundingClientRect();
    this.desktopMenu.style.setProperty('--sg-desktop-top', `${banner.top}px`);
    this.desktopMenu.style.setProperty('--sg-desktop-left', `${banner.left}px`);
    this.desktopMenu.style.setProperty('--sg-desktop-width', `${banner.width}px`);
    this.desktopMenu.style.setProperty('--sg-desktop-content-top', `${banner.height + 18}px`);
  }
  openDesktop(trigger, pointer = false) {
    if (!this.desktopMedia.matches) return;
    clearTimeout(this.desktopCloseTimer);
    this.desktopInput = pointer && this.desktopHover.matches ? 'pointer' : 'keyboard';
    this.querySelectorAll('[data-sg-desktop-target]').forEach(button => button.setAttribute('aria-expanded', String(button === trigger)));
    this.querySelectorAll('[data-sg-desktop-panel]').forEach(panel => panel.hidden = panel.dataset.sgDesktopPanel !== trigger.dataset.sgDesktopTarget);
    this.desktopTrigger = trigger;
    this.positionDesktop();
    this.desktopMenu.hidden = false;
    this.desktopMenu.scrollTop = 0;
    this.desktopBackdrop.hidden = false;
    document.body.setAttribute('data-sg-desktop-open', '');
  }
  closeDesktop(moveFocus = false) {
    clearTimeout(this.desktopCloseTimer);
    const trigger = this.desktopTrigger;
    this.querySelectorAll('[data-sg-desktop-target]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    this.desktopMenu.hidden = true;
    this.desktopBackdrop.hidden = true;
    this.desktopTrigger = null;
    document.body.removeAttribute('data-sg-desktop-open');
    if (moveFocus) trigger?.focus();
  }
  showPanel(id, moveFocus = false) {
    this.querySelectorAll('[data-sg-panel]').forEach(panel => panel.hidden = panel.dataset.sgPanel !== id);
    this.querySelector('.sg-menu__body').scrollTop = 0;
    if (moveFocus) this.querySelector(`[data-sg-panel="${id}"] button, [data-sg-panel="${id}"] a`)?.focus();
  }
  disconnectedCallback() {
    clearTimeout(this.desktopCloseTimer);
    this.controller?.abort();
    if (this.dialog?.open) this.dialog.close();
    document.body.removeAttribute('data-sg-menu-open');
    document.body.removeAttribute('data-sg-desktop-open');
  }
}
class SandlotCarousel extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.track = this.querySelector('[data-sg-track]');
    this.previous = this.querySelector('[data-sg-previous]');
    this.next = this.querySelector('[data-sg-next]');
    this.previous.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', () => this.update(), options);
    this.observer = new ResizeObserver(() => this.update());
    this.observer.observe(this.track);
    this.update();
  }
  move(direction) {
    this.track.scrollBy({ left: this.track.clientWidth * .85 * direction, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  update() {
    this.previous.disabled = this.track.scrollLeft <= 2;
    this.next.disabled = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 2;
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
}
if (!customElements.get('sandlot-header')) customElements.define('sandlot-header', SandlotHeader);
if (!customElements.get('sandlot-carousel')) customElements.define('sandlot-carousel', SandlotCarousel);
